import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { toast } from '@CommonComponents/toast'
import { Container, Row, Col } from 'react-bootstrap'
import { client } from '@Utils'
import { canvasConnect as endpoints } from '@URLS'
import { withRouter, Link } from 'react-router-dom'
import { useStateValue } from '@State'

const ConnectionTitle = ({ contextCourse, dispatch }) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(contextCourse.courseTitle || '')
  const [saving, setSaving] = useState(false)

  const startEdit = () => {
    setDraft(contextCourse.courseTitle || '')
    setEditing(true)
  }

  const cancelEdit = () => {
    setDraft(contextCourse.courseTitle || '')
    setEditing(false)
  }

  const saveEdit = async () => {
    const trimmed = draft.trim()
    if (!trimmed) {
      toast.error('Course name cannot be empty.')
      return
    }
    if (trimmed === (contextCourse.courseTitle || '')) {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      const resp = await client.post(endpoints.updateCourseTitle(), {
        cccId: contextCourse.id,
        courseTitle: trimmed,
      })
      dispatch({
        type: 'updateConnectedCourseTitle',
        payload: {
          id: contextCourse.id,
          courseTitle: resp.data.courseTitle,
        },
      })
      setEditing(false)
      toast.success('Course name updated.')
    } catch (err) {
      const msg = err?.response?.data?.error || 'Could not update course name.'
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  if (editing) {
    return (
      <div className="mb-2">
        <input
          type="text"
          className="form-control"
          value={draft}
          maxLength={250}
          autoFocus
          disabled={saving}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') saveEdit()
            else if (e.key === 'Escape') cancelEdit()
          }}
        />
        <div className="mt-2">
          <button
            type="button"
            className="btn btn-sm btn-myed btn-primary btn-primary-myed mr-2"
            disabled={saving}
            onClick={saveEdit}
          >
            Save
          </button>
          <button
            type="button"
            className="btn btn-sm btn-myed btn-default btn-default-myed"
            disabled={saving}
            onClick={cancelEdit}
          >
            Cancel
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="d-flex align-items-start mb-2">
      <h3 className="mb-0 mr-2" style={{ wordBreak: 'break-word' }}>
        {contextCourse.courseTitle || contextCourse.title}
      </h3>
      <button
        type="button"
        className="btn btn-link btn-sm p-0 mt-3"
        title="Edit course name"
        aria-label="Edit course name"
        onClick={startEdit}
      >
        <i className="fa-regular fa-pencil" />
      </button>
    </div>
  )
}

ConnectionTitle.propTypes = {
  contextCourse: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
}

const HomepageComp = props => {
  const [currentState, dispatch] = useStateValue()
  let contextId = window.location.pathname.split('/')[4]
  useEffect(() => {
    async function resetSelectedLinks() {
      await dispatch({
        type: 'assessmentInclusion',
        payload: [],
      })
      await dispatch({
        type: 'sectionSelection',
        selectedSections: [],
      })
    }
    resetSelectedLinks()
  }, [])
  useEffect(() => {
    async function getHomepageData(contextId, dispatch) {
      try {
        let resp = await client.get(endpoints.getInstructorData({ contextId }))
        await dispatch({
          type: 'updateInstructorData',
          payload: { connectedCourses: resp.data.connectedCourses },
        })
      } catch (err) {
        console.log(err)
        toast.error(err.response.data.error)
      }
    }
    getHomepageData(contextId, dispatch)
  }, [])

  return (
    <React.Fragment>
      <div className="mt-5">
        <div className="d-flex justify-content-between">
          <h1 className="me-page-title">Current Connections</h1>
          <a
            className="btn btn-myed btn-default btn-default-myed info-button mb-0 maxh-40px"
            onClick={() =>
              props.history.push(
                `/links/canvas_connect.instructor_setup/-/${contextId}/connect_resource${window.location.search}`
              )
            }
          >
            <div className="fa-regular fa-plus" /> New Connection
          </a>
        </div>
        <Container id="connected-resources">
          <Row xs={2} md={4} lg={6}>
            {currentState.connectedCourses.map((contextCourse, i) => (
              <Col md={9} key={i} className="mx-3 mb-4">
                <div className="card shadow bg-light mb-4">
                  <div className="card-title">
                    <div
                      style={{
                        backgroundImage: `url(/reader/resource/${contextCourse.coursenum}/images/reader-coverpage.jpg)`,
                        backgroundSize: 'cover',
                        maxHeight: '230px',
                        minHeight: '230px',
                      }}
                    />
                  </div>
                  <div className="card-body px-5 pb-5">
                    <ConnectionTitle
                      contextCourse={contextCourse}
                      dispatch={dispatch}
                    />
                    <h5 className="text-muted">{contextCourse.title}</h5>
                    <h5>{`Connected on ${contextCourse.createDate}`}</h5>
                    <i>{`Internal course ${contextCourse.coursenum}`}</i>
                    <div className="d-flex justify-content-between mt-5">
                      <Link
                        to={`/links/canvas_connect.instructor_setup/${contextCourse.coursenum}/${contextId}/configure_assessments${window.location.search}`}
                        className="text-dec-none"
                      >
                        <div className="fa-regular fa-bolt mr-2" /> Import Assessments
                      </Link>
                      <Link
                        className="text-dec-none"
                        to={`/links/canvas_connect.instructor_setup/${contextCourse.coursenum}/${contextId}/configure_deep_links${window.location.search}`}
                      >
                        <div className="fa-regular fa-bookmark mr-3" />
                        Create Reading Links
                      </Link>
                    </div>
                    <a
                      className="btn btn-myed btn-default btn-default-myed mt-5 btn-block bg-light-gray"
                      href={`/reader/web/${contextCourse.coursenum}/`}
                    >
                      Go to Resource <div className="fa-solid fa-long-arrow-right" />
                    </a>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}
HomepageComp.propTypes = {
  toggle: PropTypes.func,
}
export const Homepage = withRouter(HomepageComp)
